// TISA Library Database Types

export type UserRole = "student" | "teacher" | "librarian" | "parent";

export type BookType = "FIC" | "NF";

export type ThemeColor =
  | "blue"
  | "green"
  | "yellow"
  | "red"
  | "purple"
  | "orange";

export type LoanStatus = "current" | "returned" | "overdue" | "lost";

export type CopyStatus = "available" | "borrowed" | "reserved" | "lost";

export type CopyCondition = "OK" | "Damaged" | "Lost";

export type CoinTransactionType =
  | "borrow"
  | "return_ontime"
  | "overdue"
  | "lost"
  | "summary_bonus"
  | "quiz_bonus"
  | "manual";

export interface User {
  id: string;
  role: UserRole;
  name: string;
  email?: string;
  class?: string;
  pin_hash?: string;
  coins: number;
  parent_id?: string;
  created_at: string;
  last_sync?: string;
}

export interface Book {
  id: string;
  title: string;
  author: string;
  type: BookType;
  theme_color: ThemeColor;
  age_group: string;
  tags: string[];
  shelf_code: string;
  book_code: string;
  cover_url?: string;
  summary_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface Copy {
  id: string;
  book_id: string;
  copy_code: string;
  condition: CopyCondition;
  status: CopyStatus;
  last_location?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  book?: Book;
}

export interface Loan {
  id: string;
  copy_id: string;
  user_id: string;
  role_borrower: UserRole;
  start_at: string | null;
  due_at: string;
  returned_at?: string | null;
  return_condition?: CopyCondition | null;
  status: LoanStatus | null;
  overdue_days: number | null;
  created_at: string | null;
  updated_at: string | null;
  // Joined data
  copy?: Copy;
  user?: User;
}

export interface CoinTransaction {
  id: string;
  user_id: string;
  delta: number;
  type: CoinTransactionType;
  note?: string;
  created_by: string;
  settled: boolean;
  created_at: string;
  // Joined data
  user?: User;
  creator?: User;
}

export interface Summary {
  id: string;
  loan_id: string;
  text: string;
  ai_score: number;
  feedback?: string;
  approved: boolean;
  created_at: string;
  // Joined data
  loan?: Loan;
}

export interface Analytics {
  id: string;
  metric_type: "books_read" | "ontime_returns" | "coin_earnings" | "losses";
  value: number;
  period: string;
  class_id?: string;
  created_at: string;
}

// UI-specific types
export interface DashboardStats {
  totalBooks: number;
  activeLoans: number;
  overdueBooks: number;
  availableBooks: number;
}

export interface StudentStats {
  totalBooksRead: number;
  currentLoans: number;
  coinBalance: number;
  achievements: Achievement[];
  readingProgress: ReadingProgress;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: ThemeColor;
  rarity: "common" | "rare" | "epic" | "legendary";
  tisacoins: number;
  unlocked: boolean;
  unlockedAt?: string;
}

export interface ReadingProgress {
  booksReadThisMonth: number;
  themesCompleted: ThemeColor[];
  currentStreak: number;
  totalReadingTime: number;
}

// IB PYP Theme definitions
export const IB_PYP_THEMES = {
  blue: {
    name: "Who we are",
    description: "Identity, emotions, relationships",
    emoji: "👤",
  },
  green: {
    name: "Where we are in place and time",
    description: "History, geography, journeys",
    emoji: "🗺️",
  },
  yellow: {
    name: "How we express ourselves",
    description: "Creativity, art, communication",
    emoji: "🎨",
  },
  red: {
    name: "How the world works",
    description: "Science, nature, technology",
    emoji: "🌍",
  },
  purple: {
    name: "How we organize ourselves",
    description: "Economy, systems, communities",
    emoji: "🏢",
  },
  orange: {
    name: "Sharing the planet",
    description: "Sustainability, empathy, environment",
    emoji: "🌱",
  },
} as const;

// Form types
export interface BookFormData {
  title: string;
  author: string;
  type: BookType;
  theme_color: ThemeColor;
  age_group: string;
  tags: string[];
  shelf_code?: string;
}

export interface LoanFormData {
  copy_id: string;
  user_id: string;
  due_days?: number;
}

export interface SummaryFormData {
  loan_id: string;
  text: string;
}

export interface UserFormData {
  name: string;
  email?: string;
  role: UserRole;
  class?: string;
  pin?: string;
  parent_id?: string;
}

// API Response types
export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Search and filter types
export interface BookFilters {
  theme_color?: ThemeColor;
  type?: BookType;
  age_group?: string;
  tags?: string[];
  search?: string;
  available_only?: boolean;
}

export interface LoanFilters {
  status?: LoanStatus;
  overdue_only?: boolean;
  user_id?: string;
  class?: string;
  date_range?: {
    start: string;
    end: string;
  };
}

// Settings and configuration
export interface LibrarySettings {
  loan_duration_days: number;
  max_loans_per_user: number;
  coin_values: {
    borrow: number;
    return_ontime: number;
    overdue_penalty: number;
    summary_bonus: number;
    quiz_bonus: number;
  };
  school_hours: {
    start: string;
    end: string;
  };
  notification_settings: {
    due_reminder_days: number;
    overdue_reminder_interval: number;
  };
}

// Audio and accessibility
export interface AccessibilitySettings {
  sound_enabled: boolean;
  high_contrast: boolean;
  large_text: boolean;
  reduced_motion: boolean;
  dyslexia_friendly_font: boolean;
}
